import React, { Component } from "react";
import { Step, Stepper, StepLabel } from "material-ui/Stepper";
import { Dialog } from "@terraincognita/ui-core";
import _sortBy from "lodash/sortBy";
import { formatDistanceToNow } from "date-fns";
import { formatTimestamp, parseTimestamp } from "utils/helpers/timestampHelper";
import { STATUS_STEPS, STATUS_STEP_LABELS, getDisplayStatus } from "./publishStatusHelper";

// MUI v0's Step injects active/completed/last/index into all direct children.
// This wrapper prevents those props from reaching the DOM element.
function StepContent({ active, completed, last, index, children, ...rest }) {
  return <div {...rest}>{children}</div>;
}

export class PublishStatusDialog extends Component {
  constructor(props) {
    super(props);
    this.statusSteps = STATUS_STEPS;
  }

  componentDidUpdate(prevProps) {
    // Detect when dialog opens (isOpen changes from false to true)
    if (!prevProps.isOpen && this.props.isOpen && this.props.onOpen) {
      this.props.onOpen();
    }
  }

  /**
   * Get the timestamp for a specific step based on publish history
   */
  getStepTimestamp(stepIndex, publishHistory) {
    if (!publishHistory || publishHistory.length === 0) return null;

    const stepStatus = this.statusSteps[stepIndex];
    if (!stepStatus) return null;

    // Find the first entry that matches this step's display status
    const sortedHistory = _sortBy(publishHistory, "timestamp");
    for (const entry of sortedHistory) {
      const displayStatus = getDisplayStatus(entry.status);
      if (displayStatus === stepStatus) {
        return entry.timestamp;
      }
    }

    return null;
  }

  getErrorEntry(publishHistory) {
    if (!publishHistory || publishHistory.length === 0) return null;
    const lastEntry = publishHistory[publishHistory.length - 1];
    if (!lastEntry.metadata || !lastEntry.metadata.error) return null;
    return lastEntry;
  }

  getErrorStepIndex(errorEntry) {
    if (!errorEntry) return -1;
    const displayStatus = getDisplayStatus(errorEntry.status);
    return this.statusSteps.indexOf(displayStatus);
  }

  /*
  Pulls the bundleId associated with this publish event out of its
  history. Mirrors PublishHistory.groupHistoryByPublishId: regular
  publishes stamp bundleId on the "bundle-record-created" entry,
  reverts skip the build phase and stamp it on "revert-initiated"
  instead. Returns null if neither entry exists yet (e.g. very early
  in a publish that has only produced a "preparing" status with no
  bundle metadata) so the caller can suppress the row entirely.
  */
  getBundleIdFromHistory(publishHistory) {
    if (!publishHistory || publishHistory.length === 0) return null;
    const entry = publishHistory.find(
      e => e.status === "bundle-record-created" || e.status === "revert-initiated"
    );
    return entry?.metadata?.bundleId || null;
  }

  getCancelledMessage(publishHistory) {
    if (!publishHistory || publishHistory.length === 0) return null;
    const lastEntry = publishHistory[publishHistory.length - 1];
    const displayStatus = getDisplayStatus(lastEntry.status);
    if (displayStatus === "complete") return null;
    const cancelledAt = lastEntry.metadata && lastEntry.metadata.cancelledAt;
    if (!cancelledAt) return null;
    const date = parseTimestamp(cancelledAt);
    if (!date) return null;
    return "Cancelled " + formatDistanceToNow(date, { addSuffix: true }) + ".";
  }

  render() {
    const { isOpen, publishStatus, onClose, publishHistory } = this.props;
    const displayStatus = getDisplayStatus(publishStatus);
    const stepIndex = this.statusSteps.indexOf(displayStatus);
    const activeStep = displayStatus === "complete" ? this.statusSteps.length : stepIndex;
    const cancelledMessage = this.getCancelledMessage(publishHistory);
    const errorEntry = this.getErrorEntry(publishHistory);
    const errorStepIndex = this.getErrorStepIndex(errorEntry);
    const errorMetadata = errorEntry ? errorEntry.metadata : null;
    const bundleId = this.getBundleIdFromHistory(publishHistory);
    const idRowStyle = { padding: "4px 16px", fontSize: 11, color: "#999" };
    const errorLabelStyle = { fontWeight: "bold", color: "#c62828" };
    const stepDateStyle = { paddingLeft: 24, paddingTop: 8, paddingBottom: 16, fontSize: 12, color: "#666" };
    const errorDetailStyle = { color: "#c62828", fontWeight: 500 };
    const errorMetaStyle = { marginTop: 2 };

    return (
      <Dialog
        title={displayStatus !== "complete" ? "Publishing a Kiosk Update" : "Kiosk Update Complete"}
        open={isOpen}
        modal={false}
        disableCancelButton={true}
        confirmLabel="Close"
        handleConfirm={() => onClose()}
        bodyStyle={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)" }}
        className="publish-status-dialog"
      >
        <div className="table-container">
          <Stepper activeStep={activeStep} orientation="vertical">
            {this.statusSteps.map((stepKey, i) => {
              const label = STATUS_STEP_LABELS[stepKey] || stepKey;
              const isError = errorStepIndex === i;
              const timestamp = publishHistory ? this.getStepTimestamp(i, publishHistory) : null;

              return (
                <Step key={stepKey} className="table-row regular">
                  <StepLabel className="step-name">
                    {isError
                      ? <span style={errorLabelStyle}>{"Error: " + label}</span>
                      : label}
                  </StepLabel>
                  <StepContent className="step-date" style={stepDateStyle}>
                    {timestamp ? formatTimestamp(timestamp) : null}
                    {isError && errorMetadata && (
                      <div style={{ marginTop: 4 }}>
                        <div style={errorDetailStyle}>{errorMetadata.error}</div>
                        {errorMetadata.bundleId && (
                          <div style={errorMetaStyle}>Bundle ID: {errorMetadata.bundleId}</div>
                        )}
                        {errorMetadata.bundleSize && (
                          <div style={errorMetaStyle}>
                            Bundle size: {(errorMetadata.bundleSize / 1024 / 1024).toFixed(2)} MB
                          </div>
                        )}
                        {errorMetadata.fileName && (
                          <div style={errorMetaStyle}>File: {errorMetadata.fileName}</div>
                        )}
                      </div>
                    )}
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>
        </div>

        {bundleId && (
          <div style={idRowStyle}>
            Bundle ID: {bundleId}
          </div>
        )}

        {errorEntry && errorEntry.publishId && (
          <div style={idRowStyle}>
            Publish ID: {errorEntry.publishId}
          </div>
        )}

        {cancelledMessage && (
          <div className="modal-footer message-cancelled" style={{ padding: "10px 16px", backgroundColor: "#c62828", color: "#fff", fontSize: 14, fontWeight: 600, borderRadius: 4 }}>
            {cancelledMessage}
          </div>
        )}

        {displayStatus !== "complete" && !cancelledMessage && errorStepIndex < 0 && (
          <div className="modal-footer message-closeable" style={{ padding: "10px 16px", fontWeight: 500, fontSize: 14, color: "rgba(0, 0, 0, 0.87)" }}>
            <p style={{ margin: 0 }}>You can close this dialog and the publishing will continue in the background.</p>
          </div>
        )}
      </Dialog>
    );
  }
}
